import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import CircularProgress from "@material-ui/core/CircularProgress";
import api from "../../utils/api";

const initialFormState = {
  MemberCode: "",
  Email: "",
  LName: "",
  FName: "",
  MName: "",
  JobTitle: "",
  Phone: "",
  Cell: "",
  PrimaryContact: "N",
  AccountingContact: "N",
  MailChimp: "N",
};

const UserFormModal = ({
  isOpen,
  onClose,
  onAddUser,
  onUpdateUser,
  selectedUser,
}) => {
  const APIBASE_URL = useMemo(() => process.env.REACT_APP_APIBASE_URL, []);
  
  const [form, setForm] = useState(initialFormState);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  useEffect(() => {
    if (selectedUser) {
      setForm(selectedUser); // pre-fill form
    } else {
      setForm(initialFormState);
    }
  }, [selectedUser, isOpen]);

  useEffect(() => {
    setLoading(true);
    api
      .get(APIBASE_URL + "/getGreenablAllMembers")
      .then((res) => {
        setMembers(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [APIBASE_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleToggle = (e) => {
    const { name, checked } = e.target;
    setForm({ ...form, [name]: checked ?  "Y" : "N" });
  };

  const handleSubmit = async () => {
    if (!form.MemberCode || !form.Email || !form.LName || !form.FName) {
      alert("Please fill all mandatory fields.");
      return;
    }
    setButtonLoading(true);
    if (selectedUser) {
      // Update user
      try {
        const response = await fetch(`${APIBASE_URL}/insertOrUpdateGreenablUser`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        if (response.ok) {
          const updatedUser = await response.json();
          onUpdateUser(updatedUser); // update in parent
          setForm(initialFormState);
          setSnackbarMsg("User updated successfully!");
          setSnackbarOpen(true);
        } else {
          alert("Failed to update user.");
        }
      } catch (error) {
        alert("Error updating user.");
      }
      setButtonLoading(false);
    } else {
      // Add user
      try {
        await onAddUser(form);
      } finally {
        setButtonLoading(false);
      }
      setForm(initialFormState);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
    onClose(); // Close modal after snackbar
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedUser ? "Edit User" : "Add User"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required variant="outlined" size="small">
                <InputLabel>Member Company</InputLabel>
                <Select
                  name="MemberCode"
                  value={form.MemberCode}
                  onChange={handleChange}
                  label="Member Company"
                  disabled={loading}
                >
                  {loading && (
                    <MenuItem value="">
                      <CircularProgress size={20} />
                    </MenuItem>
                  )}
                  {!loading &&
                    members.map((member) => (
                      <MenuItem key={member.MemberCode} value={member.MemberCode}>
                        {member.Company}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="Email"
                value={form.Email}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                size="small"
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                name="FName"
                value={form.FName}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                name="LName"
                value={form.LName}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Middle Name"
                name="MName"
                value={form.MName}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Job Title"
                name="JobTitle"
                value={form.JobTitle}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                name="Phone"
                value={form.Phone}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Cell"
                name="Cell"
                value={form.Cell}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.PrimaryContact === "Y"}
                    onChange={handleToggle}
                    name="PrimaryContact"
                    color="primary"
                  />
                }
                label="Primary Contact"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.AccountingContact === "Y"}
                    onChange={handleToggle}
                    name="AccountingContact"
                    color="primary"
                  />
                }
                label="Accounting Contact"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="outlined" disabled={buttonLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={buttonLoading}
            startIcon={buttonLoading ? <CircularProgress size={20} /> : null}
          >
            {selectedUser ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity="success" elevation={6} variant="filled">
          {snackbarMsg}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default UserFormModal;